import axios from "axios";
import { COMPONENT_RENDERING_URL, GET_COMPANIES, GET_COMPANY_TYPES, verify_super_user_status } from "@/utils/constants/constants.js";
function parse_response_data(response_data) { return typeof response_data === "string" ? JSON.parse(response_data) : response_data; }
function sort_by_order(items = []) { return items.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)); }
function normalize_sidebar_child(sub_component) {
  return {
    ...(sub_component || {}),
    child_components: sort_by_order(sub_component?.child_components || []),
  };
}
function normalize_sidebar_component(component) {
  return {
    ...(component || {}),
    sub_components: sort_by_order(component?.sub_components || []).map(normalize_sidebar_child),
  };
}
function build_company_row(company) {
  return {
    id: company.id,
    Programme: company.name,
    Description: company.project_description,
    Type: company.project_type?.name || "-",
    "Date Created": company.date_created,
    "Assigned Administrator": company.creator?.full_name || "-",
  };
}
function resolve_component_child_url(component, find_url) {
  if (Array.isArray(component.sub_components)) {
    const sub_url = find_url(component.sub_components, "component");
    if (sub_url) return sub_url;
  }
  if (Array.isArray(component.child_components)) {
    return find_url(component.child_components, "sub");
  }
  return null;
}
function build_sidebar_item_key(parent_type, component_id) { return `${parent_type === "sub" ? "child" : "sub"}:${component_id}`; }
function get_component_url(component) { return component?.url && component.url.trim() !== "" ? component.url : null; }
function find_first_component_url(component_list, parent_type, resolve_component_url) {
  for (const component of component_list || []) {
    const component_url = resolve_component_url(component, parent_type);
    if (component_url) return component_url;
  }
  return null;
}
function clamp_page_number(page_number, total_pages) {
  const numeric_page = Number(page_number) || 1;
  const max_page = Math.max(total_pages || 1, 1);
  return Math.min(Math.max(numeric_page, 1), max_page);
}
export default {
  computed: {
    first_component_url() {
      return this.find_first_component_with_url(this.sorted_sidebar_components) || "/overview";
    },
    filtered_company_list() {
      if (!this.search_query) return this.table_safe_company_list;
      const query = this.search_query.toLowerCase();
      return this.table_safe_company_list.filter((row) => {
        return Object.values(row).some((value) => String(value).toLowerCase().includes(query));
      });
    },
    paginated_company_list() {
      const start = (this.current_page - 1) * this.items_per_page;
      return this.filtered_company_list.slice(start, start + this.items_per_page);
    },
    total_pages() {
      return Math.ceil(this.filtered_company_list.length / this.items_per_page);
    },
    visible_page_numbers() {
      const total_pages = this.total_pages;
      const current_page = this.current_page;
      if (total_pages <= 5) {
        return Array.from({ length: total_pages }, (_, index) => index + 1);
      }
      const start_page = clamp_page_number(current_page - 1, total_pages - 2);
      return [start_page, start_page + 1, start_page + 2];
    },
  },
  methods: {
    isSelectedProject(project_id) {
      return String(this.active_config_project) === String(project_id);
    },
    toggle_config_project(row) {
      const project_id = row.id;
      const company_name = row.Programme;
      if (this.isSelectedProject(project_id)) return;
      this.active_config_project = project_id;
      sessionStorage.setItem("project_id", project_id);
      sessionStorage.setItem("company_name", company_name);
      this.show_success("Configuration programme selected");
    },
    logout() {
      this.$router.push("/");
    },
    open_support_contact() {
      this.$refs.support_contact?.open_support_contact_modal?.();
    },
    set_domain_content() {
      this.hero_title = "Empowering Women , Youth & Persons With Disabilities";
      this.hero_description = "Join The Department Of Women, Youth and Persons With Disabilities and contribute to inclusive growth , empowerment and national development.";
    },
    determine_sidebar(project_id) {
      return axios.get(COMPONENT_RENDERING_URL, {
        headers: this.headers,
        params: { role: this.role, project_id },
      }).then((response) => {
        this.sidebar_components = response.data.data || [];
        this.sort_sidebar_components();
      }).catch((error) => {
        this.show_error(`Error getting sidebar configuration: ${error}`);
        return [];
      });
    },
    sort_sidebar_components() {
      this.sorted_sidebar_components = sort_by_order((this.sidebar_components || []).map(normalize_sidebar_component));
    },
    find_component_url(component, find_url, parent_type) {
      const component_url = get_component_url(component);
      if (component_url) {
        sessionStorage.setItem("sidebar_active_item_key", build_sidebar_item_key(parent_type, component.id));
        return component_url;
      }
      return resolve_component_child_url(component, find_url);
    },
    resolve_first_component_url(component, parent_type = null) {
      return this.find_component_url(
        component,
        (child_list, child_parent_type = null) =>
          find_first_component_url(child_list, child_parent_type, this.resolve_first_component_url),
        parent_type
      );
    },
    find_first_component_with_url(components) {
      return find_first_component_url(components, null, this.resolve_first_component_url);
    },
    load_data() {
      this.is_loading = true;
      return Promise.all([
        verify_super_user_status().then((value) => { this.is_super_user = value; }),
        this.get_project_types(),
        this.get_companies(),
        this.fetch_users(),
      ]).catch((error) => {
        this.show_error(`Error loading data: ${error}`);
      }).finally(() => {
        this.is_loading = false;
      });
    },
    role_class(role) {
      if (role === "Admin") return "admin-badge";
      if (role === "Owner") return "owner-badge";
      if (role === "Member") return "member-badge";
      return "default-badge";
    },
    maybe_show_project_types_loader(showLoader) {
      return showLoader ? this.show_loader("Loading programme types...") : Promise.resolve(null);
    },
    apply_project_types_response(parsed_response) {
      if (parsed_response.data && parsed_response.data.length > 0) {
        this.company_types = parsed_response.data;
        return;
      }
      this.show_error("No programme types found");
    },
    get_project_types(showLoader = false) {
      return this.maybe_show_project_types_loader(showLoader).then((loader) => {
        return axios.get(GET_COMPANY_TYPES, { headers: this.headers }).then((response) => {
          this.apply_project_types_response(parse_response_data(response.data));
        }).catch((error) => {
          this.show_error(`Error getting programme types: ${error}`);
        }).finally(() => {
          if (loader) return this.close_loader(loader);
        });
      });
    },
    maybe_show_companies_loader(showLoader) {
      return showLoader ? this.show_loader("Loading programmes...") : Promise.resolve(null);
    },
    apply_companies_response(parsed_response) {
      this.company_list = parsed_response.data || [];
      this.company_count = this.company_list.length;
      this.prep_tables();
    },
    get_companies(showLoader = false) {
      this.company_list = [];
      return this.maybe_show_companies_loader(showLoader).then((loader) => {
        return axios.get(GET_COMPANIES, {
          headers: this.headers,
          params: { include_sub_programs: true },
        }).then((response) => {
          this.apply_companies_response(parse_response_data(response.data));
        }).catch((error) => {
          this.show_error(`Error getting programmes: ${error}`);
        }).finally(() => {
          if (loader) return this.close_loader(loader);
        });
      });
    },
    build_sub_program_entry(sub) {
      const parent_id = sub.parent_program;
      const item = {
        ...build_company_row(sub),
        "Date Created": this.format_date(sub.date_created),
        _is_sub_program: true,
        _parent_id: parent_id,
      };
      return { parent_id, item };
    },
    build_sub_program_map(sub_programs) {
      const sub_program_map = Object.create(null);
      sub_programs.forEach((sub) => {
        const entry = this.build_sub_program_entry(sub);
        const current = Reflect.get(sub_program_map, entry.parent_id) || [];
        Reflect.set(sub_program_map, entry.parent_id, [...current, entry.item]);
      });
      return sub_program_map;
    },
    build_main_program_rows(main_programs, sub_program_map) {
      const formatted_data = [];
      main_programs.forEach((company) => {
        formatted_data.push({ ...build_company_row(company), "Date Created": this.format_date(company.date_created), _is_sub_program: false });
        (Reflect.get(sub_program_map, company.id) || []).forEach((sub) => {
          formatted_data.push(sub);
        });
      });
      return formatted_data;
    },
    prep_tables() {
      this.table_headers = ["Programme", "Description", "Type", "Date Created", "Assigned Administrator"];
      const main_programs = this.company_list.filter((item) => !item.parent_program);
      const sub_programs = this.company_list.filter((item) => item.parent_program);
      this.table_safe_company_list = this.build_main_program_rows(main_programs, this.build_sub_program_map(sub_programs));
      this.config_table_data = main_programs.map((company) => ({ id: company.id, Programme: company.name }));
    },
    open_company_or_sub_program(row) {
      if (row._is_sub_program) {
        this.redirect_to_sub_program_jobs(row._sub_program_id, row._sub_program_name, row._project_id);
        return;
      }
      this.redirect_to_overview(row._project_id || row.id, row.Programme);
    },
    redirect_to_overview(project_id, company_name) {
      this.project_id = project_id;
      this.company_name = company_name;
      sessionStorage.setItem("project_id", project_id);
      sessionStorage.setItem("company_name", company_name);
      sessionStorage.removeItem("sub_program_id");
      sessionStorage.removeItem("sub_program_name");
      sessionStorage.removeItem("sub_program_context");
      this.determine_sidebar(project_id).then(() => {
        this.$router.push({ path: this.first_component_url });
      }).catch(() => {
        this.$router.push({ path: "/overview" });
      });
    },
    redirect_to_config(project_id = null, company_name = null) {
      this.project_id = project_id;
      this.company_name = company_name;
      sessionStorage.setItem("project_id", project_id);
      sessionStorage.setItem("company_name", company_name);
      sessionStorage.removeItem("sub_program_id");
      sessionStorage.removeItem("sub_program_name");
      sessionStorage.removeItem("sub_program_context");
      this.$router.push({ path: "/configure_company_management" });
    },
  },
};
